import {isCriticalAttackRoll} from '../../../shared/rules/champion.mjs';
const ENTITY_TYPES = new Set(['character', 'monster', 'unknown']);
const ATTACK_KINDS = new Set(['weapon', 'unarmed', 'spell', 'feature', 'monster-action', 'unknown']);
const ABILITIES = new Set(['str', 'dex', 'con', 'int', 'wis', 'cha']);
const ROLL_MODES = new Set(['normal', 'advantage', 'disadvantage']);
const SOURCE_TYPES = new Set(['base', 'feature', 'spell', 'other']);
const CRITICAL_POLICIES = new Set(['double', 'unchanged']);
const DAMAGE_TYPE_MODES = new Set(['inherit', 'explicit']);

const requiredString = (value, name) => {
  if (typeof value !== 'string' || !value.trim()) throw new Error(`${name} is required.`);
  return value;
};

const nullableString = (value, name) => {
  if (value == null) return null;
  if (typeof value !== 'string' || !value.trim()) throw new Error(`${name} must be a nonempty string or null.`);
  return value;
};

const nullableInteger = (value, name, minimum) => {
  if (value == null) return null;
  if (!Number.isInteger(value) || value < minimum) throw new Error(`${name} must be an integer of at least ${minimum} or null.`);
  return value;
};

const boolean = (value, name) => {
  if (typeof value !== 'boolean') throw new Error(`${name} must be a boolean.`);
  return value;
};

const stringArray = (value, name) => {
  if (!Array.isArray(value) || value.some((item) => typeof item !== 'string' || !item.trim())) {
    throw new Error(`${name} must be an array of nonempty strings.`);
  }
  return [...value];
};

/** Build the narrow, post-hit snapshot consumed by future attack features. */
export const buildResolvedHitContext = (input = {}) => {
  const { attacker = {}, target = {}, attack = {}, roll = {}, combat = {} } = input;
  const resolutionId = requiredString(input.resolutionId, 'resolutionId');
  const attackerId = requiredString(attacker.combatantId, 'attacker.combatantId');
  const targetId = requiredString(target.combatantId, 'target.combatantId');

  if (!ENTITY_TYPES.has(attacker.entityType)) throw new Error('attacker.entityType is invalid.');
  if (!ENTITY_TYPES.has(target.entityType)) throw new Error('target.entityType is invalid.');
  const attackId = requiredString(attack.id, 'attack.id');
  if (!ATTACK_KINDS.has(attack.kind)) throw new Error('attack.kind is invalid.');
  if (attack.ability != null && !ABILITIES.has(attack.ability)) throw new Error('attack.ability is invalid.');
  if (!Number.isFinite(attack.abilityModifier)) throw new Error('attack.abilityModifier must be a finite number.');
  if (!Number.isFinite(attack.proficiencyBonus) || attack.proficiencyBonus < 0) throw new Error('attack.proficiencyBonus must be a nonnegative finite number.');
  if (!Number.isFinite(target.armorClass)) throw new Error('target.armorClass must be a finite number.');

  if (!ROLL_MODES.has(roll.mode)) throw new Error('roll.mode is invalid.');
  const advantageSources = stringArray(roll.advantageSources, 'roll.advantageSources');
  const disadvantageSources = stringArray(roll.disadvantageSources, 'roll.disadvantageSources');
  if (!Number.isInteger(roll.naturalRoll) || roll.naturalRoll < 1 || roll.naturalRoll > 20) {
    throw new Error('roll.naturalRoll must be an integer from 1 to 20.');
  }
  if (!Number.isFinite(roll.total)) throw new Error('roll.total must be a finite number.');
  if (roll.hit !== true) throw new Error('ResolvedHitContext can only describe a hit.');
  boolean(roll.critical, 'roll.critical');
  // `automaticCritical`: a critical from the target's state, not the d20 (any hit on an Unconscious creature from 5 feet).
  if (roll.critical !== (isCriticalAttackRoll(attacker.entity, attack, roll.naturalRoll) || roll.automaticCritical === true)) throw new Error('roll.critical does not match the natural roll.');

  const classification = ['isWeaponAttack', 'isMelee', 'isRanged', 'isFinesse']
    .reduce((result, key) => ({ ...result, [key]: boolean(attack[key], `attack.${key}`) }), {});
  if (classification.isMelee && classification.isRanged) throw new Error('An attack cannot be both melee and ranged.');

  const campaignId = nullableString(combat.campaignId, 'combat.campaignId');
  const round = nullableInteger(combat.round, 'combat.round', 1);
  const turnSequence = nullableInteger(combat.turnSequence, 'combat.turnSequence', 0);
  const activeCombatantId = nullableString(combat.activeCombatantId, 'combat.activeCombatantId');
  if (activeCombatantId && turnSequence == null) throw new Error('combat.turnSequence is required during active combat.');
  const participantIds = stringArray(combat.participantIds, 'combat.participantIds');

  return {
    resolutionId,
    attacker: {
      combatantId: attackerId,
      entityType: attacker.entityType,
      entity: attacker.entity ?? null,
      participant: attacker.participant ?? null,
    },
    target: {
      combatantId: targetId,
      entityType: target.entityType,
      entity: target.entity ?? null,
      participant: target.participant ?? null,
      armorClass: target.armorClass,
    },
    attack: {
      id: attackId,
      name: attack.name ?? '',
      kind: attack.kind,
      source: attack.source ?? null,
      ability: attack.ability ?? null,
      abilityModifier: attack.abilityModifier,
      proficiencyBonus: attack.proficiencyBonus,
      damageType: attack.damageType ?? null,
      ...classification,
    },
    roll: {
      mode: roll.mode,
      advantageSources,
      disadvantageSources,
      naturalRoll: roll.naturalRoll,
      total: roll.total,
      hit: true,
      critical: roll.critical,
    },
    combat: {
      campaignId,
      round,
      turnSequence,
      activeCombatantId,
      isAttackerTurn: activeCombatantId === attackerId,
      participantIds,
      ...(combat.turnFeatureUsage ? { turnFeatureUsage: combat.turnFeatureUsage } : {}),
    },
  };
};

/** Validate and copy one unrolled damage request. */
export const normalizeDamageComponent = (component = {}) => {
  const id = requiredString(component.id, 'Damage component id');
  const label = requiredString(component.label, 'Damage component label');
  const source = component.source;
  if (!source || !SOURCE_TYPES.has(source.type)) throw new Error(`Damage component "${id}" has an invalid source type.`);
  const featureId = nullableString(source.featureId, `Damage component "${id}" source.featureId`);

  let dice = null;
  if (component.dice != null) {
    if (!Number.isInteger(component.dice.count) || component.dice.count <= 0) {
      throw new Error(`Damage component "${id}" dice count must be a positive integer.`);
    }
    if (!Number.isInteger(component.dice.sides) || component.dice.sides < 2) {
      throw new Error(`Damage component "${id}" die sides must be an integer of at least 2.`);
    }
    dice = { count: component.dice.count, sides: component.dice.sides };
  }
  const flat = component.flat === undefined ? 0 : component.flat;
  if (!Number.isFinite(flat) || flat < 0) throw new Error(`Damage component "${id}" flat damage must be a nonnegative finite number.`);
  if (!dice && flat === 0) throw new Error(`Damage component "${id}" must deal dice or flat damage.`);

  const damageType = component.damageType;
  if (!damageType || !DAMAGE_TYPE_MODES.has(damageType.mode)) throw new Error(`Damage component "${id}" has an invalid damage-type mode.`);
  if (damageType.mode === 'explicit') requiredString(damageType.value, `Damage component "${id}" damage type`);
  if (damageType.mode === 'inherit' && Object.prototype.hasOwnProperty.call(damageType, 'value')) {
    throw new Error(`Damage component "${id}" cannot give an inherited damage type a value.`);
  }
  const critical = component.critical;
  if (!critical || !CRITICAL_POLICIES.has(critical.dice)) throw new Error(`Damage component "${id}" has an invalid critical dice policy.`);

  return {
    id,
    label,
    source: { type: source.type, featureId },
    dice,
    flat,
    damageType: damageType.mode === 'inherit' ? { mode: 'inherit' } : { mode: 'explicit', value: damageType.value },
    critical: { dice: critical.dice },
  };
};

/** Normalize a resolution's components and enforce their per-resolution identity. */
export const normalizeDamageComponents = (components) => {
  if (!Array.isArray(components)) throw new Error('Damage components must be an array.');
  const normalized = components.map(normalizeDamageComponent);
  const ids = new Set();
  normalized.forEach(({ id }) => {
    if (ids.has(id)) throw new Error(`Damage component id "${id}" is duplicated.`);
    ids.add(id);
  });
  return normalized;
};

const randomDie = (sides) => Math.floor(Math.random() * sides) + 1;

/** Roll normalized damage requests without merging away component identity. */
export const rollDamageComponents = (components, { critical = false, inheritedDamageType = null, rollDie = randomDie, damageDieFloor = 1 } = {}) => {
  if (typeof critical !== 'boolean') throw new Error('critical must be a boolean.');
  if (inheritedDamageType != null && (typeof inheritedDamageType !== 'string' || !inheritedDamageType.trim())) {
    throw new Error('inheritedDamageType must be a nonempty string or null.');
  }
  if (typeof rollDie !== 'function') throw new Error('rollDie must be a function.');

  return normalizeDamageComponents(components).map((component) => {
    const baseDiceCount = component.dice?.count || 0;
    const criticalDiceCount = critical && component.critical.dice === 'double' ? baseDiceCount : 0;
    const rollCount = baseDiceCount + criticalDiceCount;
    const rolls = Array.from({ length: rollCount }, () => {
      const result = rollDie(component.dice.sides);
      if (!Number.isInteger(result) || result < 1 || result > component.dice.sides) {
        throw new Error(`Dice roller returned an invalid d${component.dice.sides} result.`);
      }
      return result;
    });
    const effectiveRolls = rolls.map(roll => Math.max(roll, damageDieFloor));
    const diceTotal = effectiveRolls.reduce((sum, roll) => sum + roll, 0);
    return {
      ...component,
      resolvedDamageType: component.damageType.mode === 'explicit' ? component.damageType.value : inheritedDamageType,
      rolls,
      ...(damageDieFloor > 1 ? { effectiveRolls } : {}),
      baseDiceCount,
      criticalDiceCount,
      diceTotal,
      flatTotal: component.flat,
      total: diceTotal + component.flat,
    };
  });
};

/**
 * Describe the real dice needed by damage components for a visual/authoritative
 * roll. Flat-only components deliberately produce no request.
 */
export const buildDamageComponentDiceRequests = (components, { critical = false, inheritedDamageType = null } = {}) =>
  normalizeDamageComponents(components).flatMap((component) => {
    if (!component.dice?.count) return [];
    const criticalDiceCount = critical && component.critical.dice === 'double'
      ? component.dice.count
      : 0;
    return [{
      count: component.dice.count + criticalDiceCount,
      sides: component.dice.sides,
      componentId: component.id,
      label: component.label,
      damageType: component.damageType.mode === 'explicit'
        ? component.damageType.value
        : inheritedDamageType,
      baseDiceCount: component.dice.count,
      criticalDiceCount,
    }];
  });

